import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { X, ZoomIn } from 'lucide-react'

import Section from '../components/ui/Section'
import GlassCard from '../components/ui/GlassCard'
import MediaDisplay from '../components/ui/MediaDisplay'
import { useStore } from '../store/useStore'

const Gallery = () => {
  const { t } = useTranslation()
  const { galleryImages } = useStore()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedImage, setSelectedImage] = useState(null)

  // Debug: Afficher les images dans la console
  console.log('Gallery - Images chargées:', galleryImages)

  // Correspondance avec les catégories admin
  const categories = [
    { id: 'all', label: 'Toutes les photos' },
    { id: 'events', label: 'Événements' },
    { id: 'residences', label: 'Résidences' },
    { id: 'activities', label: 'Activités' },
    { id: 'team', label: 'Équipe' },
  ]

  // Obtenir toutes les images de toutes les catégories
  const allImages = []
  Object.keys(galleryImages).forEach(category => {
    if (Array.isArray(galleryImages[category])) {
      galleryImages[category].forEach(image => {
        allImages.push({
          id: image.id,
          title: image.title || 'Image sans titre',
          url: image.imageUrl || image.url, // Support des deux formats
          category: category,
          description: image.description,
          date: image.date,
          location: image.location
        })
      })
    }
  })

  // Filtrer les images selon la catégorie sélectionnée
  const filteredImages = selectedCategory === 'all'
    ? allImages
    : allImages.filter(img => img.category === selectedCategory)

  return (
    <div className="min-h-screen pt-32 pb-20">
      <Section
        title={t('gallery.title')}
        subtitle={t('gallery.subtitle')}
      >
        {/* Category Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="glass-card p-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
                      : 'glass-btn'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Image Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          <AnimatePresence>
            {filteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ delay: index * 0.05 }}
              >
                <GlassCard
                  className="overflow-hidden group cursor-pointer p-0"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <MediaDisplay
                      src={image.url}
                      alt={image.title}
                      className="w-full h-full group-hover:scale-110 transition-transform duration-500"
                      showControls={false}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <ZoomIn className="w-12 h-12 text-primary-600 mb-2" />
                      <p className="text-black font-semibold text-center px-4 bg-white/90 rounded-lg py-2">
                        {image.title}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {filteredImages.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <p className="text-xl text-white/60">
              Aucune image dans cette catégorie
            </p>
          </motion.div>
        )}
      </Section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 cursor-pointer"
            />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="relative max-w-7xl w-full"
              >
                <MediaDisplay
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-[90vh] object-contain rounded-2xl"
                  showControls={true}
                />
                
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent rounded-b-2xl">
                  <h3 className="text-2xl font-bold text-white text-center">
                    {selectedImage.title}
                  </h3>
                </div>

                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-4 right-4 glass-btn p-3"
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Gallery

